/** @format */

import { Fragment, useEffect, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/solid";
import { AuthInterface, Chain, LoginType } from "interfaces";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reducer/store";
import { setLoginType } from "reducer/logintype.slice";
import clsx from "clsx";
import { api, utils } from "services";
import Config from "config";

export const getWalletOptions = (wallets: any) => {
  let data: any[] = [];
  if (
    wallets[LoginType.BSC][AuthInterface.identity] &&
    !Config.MainnetUnsupportedChain.includes(LoginType.BSC)
  ) {
    data.push({
      value: Chain.BSC,
      name: `BNB Address: ${utils.displayWalletAddress(
        wallets[LoginType.BSC][AuthInterface.identity]
      )}`,
      icon: "/assets/images/wallets/bsc.png",
    });
  }
  if (
    wallets[LoginType.Terra][AuthInterface.identity] &&
    !Config.MainnetUnsupportedChain.includes(LoginType.Terra)
  ) {
    data.push({
      value: Chain.Terra,
      name: `Terra Address: ${utils.displayWalletAddress(
        wallets[LoginType.Terra][AuthInterface.identity]
      )}`,
      icon: "/assets/images/wallets/terra.png",
    });
  }
  if (
    wallets[LoginType.Avalanche][AuthInterface.identity] &&
    !Config.MainnetUnsupportedChain.includes(LoginType.Avalanche)
  ) {
    data.push({
      value: LoginType.Avalanche,
      name: `AVAX Address: ${utils.displayWalletAddress(
        wallets[LoginType.Avalanche][AuthInterface.identity]
      )}`,
      icon: "/assets/images/wallets/avalanche.png",
    });
  }
  // if (wallets[LoginType.Okex][AuthInterface.identity] && !Config.MainnetUnsupportedChain.includes(LoginType.Okex)) {
  //     data.push({
  //         value: LoginType.Okex,
  //         name: `OKX Address: ${utils.displayWalletAddress(wallets[LoginType.Okex][AuthInterface.identity])}`,
  //         icon: '/assets/images/wallets/okex.png',
  //     })
  // }
  if (
    wallets[LoginType.Aura][AuthInterface.identity] &&
    !Config.MainnetUnsupportedChain.includes(LoginType.Aura)
  ) {
    data.push({
      value: LoginType.Aura,
      name: `Aura Address: ${utils.displayWalletAddress(
        wallets[LoginType.Aura][AuthInterface.identity]
      )}`,
      icon: "/assets/images/wallets/aura.png",
    });
  }
  return data;
};

export default function Selection({
  className,
  options,
  setValue,
  setAvailable,
  disabled = false,
  theme = "light",
}: {
  className?: string;
  options: any[];
  setValue?: any;
  setAvailable?: any;
  disabled?: boolean;
  theme?: "dark" | "light";
}) {
  const [selected, setSelected] = useState(options[0]);
  const loginType = useSelector((state: RootState) => state.logintype);
  const dispatch = useDispatch();

  useEffect(() => {
    let defaultValues = options.filter(
      (option) => option.value === loginType || option.value === -1
    );
    setSelected(
      defaultValues.length > 0 ? defaultValues[0] : options[LoginType.BSC]
    );
  }, [loginType]);

  return (
    <Listbox
      disabled={disabled}
      value={selected}
      onChange={async (e) => {
        if (e.value > -1) {
          await api.checkNetwork(e.value, "select-input.tsx");
          dispatch(setLoginType(e.value));
        }
        setSelected(e);
        setValue(e.value);
      }}
    >
      {({ open }) => (
        <>
          <div className={"mt-1 relative"}>
            <Listbox.Button
              className={`
                        ${
                          theme === "dark"
                            ? "bg-[#1F1D24] border-input"
                            : "bg-white border-orange border-t-3 border-l-0"
                        }
                        relative w-full border rounded-md shadow-sm pl-3 pr-10 py-4 text-left cursor-default sm:text-sm`}
            >
              <div className="flex absolute inset-y-0 left-0 pl-3 pointer-events-none flex-col">
                <span
                  className={`${
                    theme === "dark" ? "text-[#999999]" : "text-gray"
                  } text-sm mt-1 mr-2`}
                >
                  Wallet address
                </span>
                <span
                  className={`flex items-center ${
                    theme === "dark" ? "text-[#E4E4E4]" : "text-dark"
                  } font-semibold`}
                >
                  {selected?.icon && (
                    <img
                      src={selected.icon}
                      alt=""
                      className="flex-shrink-0 h-6 w-6 rounded-full"
                    />
                  )}
                  <span className="ml-2 block truncate">{selected?.name}</span>
                </span>
              </div>

              <span className="flex items-center h-6"></span>

              <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <img
                  src="/assets/images/icons/dark-select-icon.svg"
                  alt="select"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-56 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {options.map((item, index) => (
                  <Listbox.Option
                    key={index}
                    className={({ active }) =>
                      clsx(
                        active ? "text-white bg-indigo-600" : "text-gray",
                        "cursor-default select-none relative py-2 pl-3 pr-9"
                      )
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <div key={index}>
                        <div className="flex items-center">
                          <img
                            src={item.icon}
                            alt=""
                            className="flex-shrink-0 h-6 w-6 rounded-full"
                          />
                          <span
                            className={clsx(
                              selected ? "font-semibold" : "font-normal",
                              "ml-3 block truncate"
                            )}
                          >
                            {item.name}
                          </span>
                        </div>

                        {selected ? (
                          <span
                            className={clsx(
                              active ? "text-white" : "text-indigo-600",
                              "absolute inset-y-0 right-0 flex items-center pr-4"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}
